import React, { useState, useEffect } from 'react'
import { TextField, Tooltip } from '@material-ui/core'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import cn from 'classnames'
import { getFormattedDate, getDateFormat } from 'helpers/dates'
import NestedSelectInput from 'components/common/Inputs/NestedSelectInput'
import { ReactComponent as DuplicateIcon } from 'assets/img/duplicate.svg'
import { ReactComponent as BinIcon } from 'assets/img/bin.svg'
import { ReactComponent as ArrowRight } from 'assets/img/arrow-right.svg'
import {
  fetchProjectedSitesManagerSnapshots,
  updateProjectedSitesManagerSnapshot,
} from 'reducers/projectedSitesManager/reducer'
import withPromise from 'helpers/withPromise'
import { FIELD_NAME } from 'constants/jobsTriggers'
import {
  StyledErrorMessage,
  StyledIconWrap,
  StyledInputHolder,
} from 'components/common/Inputs/styles'
import { Flex } from 'components/common/styles'
import messages from '../translate'

function SingleSnapshot({
  intl: { formatMessage },
  licensee: { usaDateFormat },
  projectedSitesManagerAdmin,
  id,
  dateCreated,
  dateUpdated,
  name,
  permissions,
  isLoadSnapshots,
  setSnapshotToDelete,
  onUpdateSnapshot,
  usersAndGroups,
  updateProjectedSitesManagerSnapshot,
  fetchProjectedSitesManagerSnapshots,
  handleCloseModal,
  selectedSiteType,
  selectedSnapshot,
  setSnapshot,
}) {
  const isDisabled = isLoadSnapshots || !projectedSitesManagerAdmin
  const usersAndGroupsData = usersAndGroups.toJS()
  const dateFormat = getDateFormat(usaDateFormat)
  const isCurrentSnapshot = +selectedSnapshot === id

  const [snapshotsName, setSnapshotsName] = useState({})
  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    setSnapshotsName(name)
  }, [name])

  const formatPermissionsData = data => {
    const formattedData = []

    Object.keys(data).forEach(key => {
      formattedData.push({
        title: key,
        options: data[key].map(({ id, name }) => ({
          value: `${key}_${id}`,
          label: name,
        })),
      })
    })

    return formattedData
  }

  const formatPermissionsValues = values => {
    const formattedValues = []

    Object.keys(values).forEach(key => {
      values[key].forEach(({ id }) => {
        formattedValues.push(`${key}_${id}`)
      })
    })

    return formattedValues
  }

  const getNameValidationErrorMessage = value => {
    if (!value) {
      return formatMessage(messages.invalidEmptyValue)
    }

    if (value.length > 255) {
      return formatMessage(messages.invalidTitle)
    }

    return ''
  }

  const formattedUsersAndGroups = formatPermissionsData(usersAndGroupsData)
  const formattedPermissions = formatPermissionsValues(permissions)

  const onChangeName = value => {
    setSnapshotsName(value)

    if (!value || value.length > 255) {
      setValidationErrors({
        ...validationErrors,
        [FIELD_NAME]: getNameValidationErrorMessage(value),
      })
    } else {
      const newValidationErrors = { ...validationErrors }

      delete newValidationErrors[FIELD_NAME]

      setValidationErrors(newValidationErrors)
    }
  }

  const onSaveName = () => {
    if (
      !Object.prototype.hasOwnProperty.call(validationErrors, FIELD_NAME) &&
      snapshotsName !== name
    ) {
      withPromise(updateProjectedSitesManagerSnapshot, {
        id,
        data: {
          name: snapshotsName.trim(),
        },
      }).then(response => {
        if (response.errors) {
          setValidationErrors({
            ...validationErrors,
            ...response.errors,
          })
        }

        fetchProjectedSitesManagerSnapshots()
      })
    }
  }

  const onSavePermissions = values => {
    const newPermissions = {
      users: [],
      userGroups: [],
    }

    values.forEach(value => {
      const [key, id] = value.split('_')

      newPermissions[key].push(+id)
    })

    withPromise(updateProjectedSitesManagerSnapshot, {
      id,
      data: {
        permissions: newPermissions,
      },
    }).then(() => {
      fetchProjectedSitesManagerSnapshots()
    })
  }

  return (
    <tr key={id} className={cn({ active: isCurrentSnapshot })}>
      <td>
        <StyledInputHolder>
          <TextField
            required
            value={snapshotsName}
            className={cn({
              error: Object.prototype.hasOwnProperty.call(
                validationErrors,
                FIELD_NAME,
              ),
            })}
            disabled={isDisabled}
            onChange={({ target: { value } }) => {
              onChangeName(value)
            }}
            onBlur={onSaveName}
          />
          {validationErrors[FIELD_NAME] && (
            <Tooltip title={validationErrors[FIELD_NAME]}>
              <StyledErrorMessage>
                {validationErrors[FIELD_NAME]}
              </StyledErrorMessage>
            </Tooltip>
          )}
        </StyledInputHolder>
      </td>
      <td>{getFormattedDate(dateCreated, dateFormat)}</td>
      <td>{getFormattedDate(dateUpdated, dateFormat)}</td>
      {projectedSitesManagerAdmin && (
        <td>
          <NestedSelectInput
            disabled={isDisabled}
            multiple
            hasSelectAllOption
            hasSearchField
            titlesTranslatable
            value={formattedPermissions}
            options={formattedUsersAndGroups}
            label={formatMessage(messages.chooseUsers)}
            onClose={values => {
              onSavePermissions(values)
            }}
          />
        </td>
      )}
      <td>
        <Flex>
          {projectedSitesManagerAdmin && (
            <>
              <StyledIconWrap
                onClick={() => {
                  onUpdateSnapshot({
                    id,
                    method: 'PUT',
                  })
                }}>
                <Tooltip
                  placement="top"
                  title={formatMessage(messages.duplicate)}>
                  <DuplicateIcon />
                </Tooltip>
              </StyledIconWrap>
              <StyledIconWrap
                disabled={isCurrentSnapshot}
                onClick={() => {
                  setSnapshotToDelete(id)
                }}>
                <Tooltip placement="top" title={formatMessage(messages.delete)}>
                  <BinIcon />
                </Tooltip>
              </StyledIconWrap>
            </>
          )}
          <StyledIconWrap
            disabled={isCurrentSnapshot}
            onClick={() => {
              if (!isCurrentSnapshot) {
                setSnapshot(null, selectedSiteType, id)
                handleCloseModal()
              }
            }}>
            <ArrowRight />
          </StyledIconWrap>
        </Flex>
      </td>
    </tr>
  )
}

export default connect(
  state => ({
    licensee: state.getIn(['profile', 'profile', 'licensee']),
    projectedSitesManagerAdmin: state.getIn([
      'profile',
      'profile',
      'projectedSitesManagerAdmin',
    ]),
    usersAndGroups: state.getIn(['projectedSitesManager', 'usersAndGroups']),
  }),
  {
    updateProjectedSitesManagerSnapshot,
    fetchProjectedSitesManagerSnapshots,
  },
)(injectIntl(SingleSnapshot))
